import * as _ from "lodash-es";
import { useEffect, useState } from "react";
import IOnGetOrdersDetailReportDTO from "../../../../models/DTO/IOnGetOrdersDetailReportDTO";
import IAddress from "../../../../models/IAddress";
import IOrder from "../../../../models/IOrder";
import IShipment_Group from "../../../../models/IShipment_Group";
import IShipment_Groups_Orders_Map from "../../../../models/IShipment_Groups_Orders_Map";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import IVendorModel from "../../../../models/IVendorModel";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import UserService from "../../../../services/UserService";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OrdersDetailReportOrderRow from "./OrdersDetailReportOrderRow/OrdersDetailReportOrderRow";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import ReportService from "@services/ReportService";

type Props = {
    processedReport: IProcessed_Report;
};

export const OrdersDetailReport = (props: Props) => {
    const { supplierModel, userModel, vendorModel } = useAppContextNonNull();
    const [data, setData] = useState<IOnGetOrdersDetailReportDTO | undefined>(undefined);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [shipment_groups, setShipmentGroups] = useState<IShipment_Group[]>([]);
    const [shipment_groups_orders_map, setShipmentGroupsOrdersMap] = useState<IShipment_Groups_Orders_Map[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [oneOrderPerPage, setOneOrderPerPage] = useState<boolean>(false);
    const [downloadingExcel, setDownloadingExcel] = useState<boolean>(false);

    useEffect(() => {
        setData(props.processedReport.report_data);
        setOrders(data?.orders || []);
        setShipmentGroups(data?.shipmentGroups || []);
        setShipmentGroupsOrdersMap(data?.shipmentGroupOrdersMap || []);
        setStartDate(data?.periodStart || "");
        setEndDate(data?.periodEnd || "");
    }, [props.processedReport.report_data]);

    const processDate = (dateStr: string) => {
        if (!dateStr.length) {
            return null;
        }

        return UserService.parseDate(dateStr);
    };

    const getOrderState = (order: IOrder) => {
        const state = order.order_state_id;

        switch (state) {
            case OrderStatesEnum.CONFIRMED:
                return "Confirmed";
        }

        return _.startCase((order?.Order_State?.name || "").toLowerCase());
    };

    const exportCsv = () => {
        const rows = [
            [
                "Order #",
                "Status",
                "Order Created At",
                "Order Submitted At",
                "User Name",
                "User Email",
                "Customer Name",
                "Order Category",
                "Ship To Address",
                "Line #",
                "SKU",
                "Item Description",
                "Item Category",
                "Order Qty.",
            ],
        ];

        for (const order of orders) {
            for (const order_item of order.Order_Items) {
                const shipTo = order.Ship_To || order?.Vendor?.Ship_To || ({} as IAddress);
                const row = [
                    `${order.name}`,
                    `${getOrderState(order)}`,
                    `${processDate(order.created_at as string)}`,
                    `${processDate(order?.submit_date || "") || ""}`,
                    `${order?.User?.name}`,
                    `${order?.User?.email}`,
                    `${order?.Vendor?.name}`,
                    `${order?.Category?.name || ""}`,
                    `${shipTo.address_line_one} ${shipTo.address_line_two || ""} ${shipTo.address_line_three || ""}\t${shipTo.city} ${
                        shipTo.state
                    } ${shipTo.postal_code}\t${shipTo.country}`,
                    `${order_item.item_no}`,
                    `${order_item?.Catalog_Item?.sku}`,
                    `${order_item?.Catalog_Item?.description || ""}`,
                    `${order_item?.Catalog_Item?.Category?.name || ""}`,
                    `${order_item.qty}`,
                ];

                console.log("row", row);

                rows.push(row);
            }
        }

        const csvContent = rows.map((e) => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const fileName = `Order Detail Report - ${processDate(startDate)} - ${processDate(endDate)}.csv`;

        link.download = fileName;
        link.click();
    };

    const handleDownloadExcel = async () => {
        if (downloadingExcel) {
            return;
        }

        setDownloadingExcel(true);

        try {
            await ReportService.downloadExcelReport(props.processedReport.guid, userModel, supplierModel, vendorModel);
        } catch (e) {
            console.error("Failed to download report excel", e);
        } finally {
            setDownloadingExcel(false);
        }
    };

    return (
        <div className="OrdersDetailReport">
            <div className={"mb-3"}>
                <OwTitle title={"orders_detail_report"} />
            </div>
            <div className={"mb-5"}>
                <b>
                    <OwLabel className={"me-2"} title={"report_period"} />
                </b>
                <span>
                    {processDate(startDate)} - {processDate(endDate)}
                </span>
                {data?.category && (
                    <div className={"mt-2"}>
                        <OwLabel className={"me-2"} title={"order_category"} isBold={true} />
                        {data.category.name}
                    </div>
                )}
                {props.processedReport.excel_file_url ? (
                    <div className={"d-print-none mt-2"}>
                        <OwLabel
                            leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fad fa-cloud-download"}
                            className={"pointer"}
                            title={downloadingExcel ? "generating_excel" : "download_excel"}
                            fn={handleDownloadExcel}
                            noBottomMargin={true}
                        />
                    </div>
                ) : (
                    <div className={"d-print-none mt-2"}>
                        <OwLabel
                            leftIconClass={"fad fa-cloud-download"}
                            className={"pointer"}
                            title={"export_csv"}
                            fn={exportCsv}
                            noBottomMargin={true}
                        />
                    </div>
                )}
                <div className={"d-print-none mt-2"}>
                    <OwToggle
                        name={"oneOrderPerPage"}
                        label={"page_break_after_each_order"}
                        active={oneOrderPerPage}
                        fn={setOneOrderPerPage}
                        labelOnRight={true}
                    />
                </div>
            </div>
            {orders.map((order) => {
                return (
                    <div key={order.guid} className={oneOrderPerPage ? "break-after-page" : ""}>
                        <OrdersDetailReportOrderRow order={order} />
                        {!oneOrderPerPage && <hr className={"my-4"} />}
                    </div>
                );
            })}
        </div>
    );
};

export default OrdersDetailReport;
